import { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Download, Loader, MoreHorizontal, Pencil, Trash } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Link } from "react-router";

import { MultiFileUploadButton } from "@/components/multi-file-upload-button";
import { ResponsiveButton } from "@/components/responsive-button";
import { ServerDataTable } from "@/components/server-data-table";
import { SkuImage } from "@/components/skus/sku-image";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExportImagesMutation } from "@/data/mutations";
import { useGetImagesQuery } from "@/data/queries";
import { Image } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { canEditAsset } from "@/lib/permissions";
import { FileType } from "@/lib/uploads";
import useStore from "@/state";

const columns: ColumnDef<Image>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
  },
  {
    id: "file_name",
    accessorKey: "file_name",
    header: "File Name",
    size: 320,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 w-full">
          <Link
            to={`${row.original.url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <SkuImage
              src={row.original.url}
              alt={row.original.file_name}
              srcSetWidths={[200, 400]}
              className="max-w-24 max-h-24 min-w-24 min-h-24"
              hasSquareCrop
            />
          </Link>
          <Link
            to={`${row.original.url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="min-w-0">
              <p className="font-medium truncate">{row.original.file_name}</p>
              <p className="text-sm text-muted-foreground truncate">
                {row.original.url}
              </p>
            </div>
          </Link>
        </div>
      );
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: "Uploaded at",
    cell: ({ row }) => {
      return (
        <div>
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    accessorKey: "actions",
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { openDialog } = useDialogState();

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() =>
                openDialog({
                  name: "update-image",
                  params: row.original,
                })
              }
            >
              <Pencil /> Edit File Name (Coming Soon)
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                openDialog({
                  name: "delete-image",
                  params: row.original,
                })
              }
            >
              <Trash /> Delete Image
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

const IMAGES_DEFAULT_SORT = { column: "id", direction: "desc" } as const;

export const Images = () => {
  const { session } = useStore();
  const exportMutation = useExportImagesMutation({
    defaultSort: IMAGES_DEFAULT_SORT,
  });
  const { data, isError, error, isFetching, refetch } = useGetImagesQuery();

  const hasPermission = useMemo(
    () => canEditAsset(session?.permissions),
    [session?.permissions],
  );

  const handleFinish = useCallback(() => refetch(), [refetch]);

  const handleExport = async () => {
    exportMutation.mutateAsync();
  };

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div>
      <div className="flex justify-between border-b pb-2">
        <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
          Images
        </h2>
        <MultiFileUploadButton
          fileType={FileType.IMAGE}
          onFinish={handleFinish}
        />
      </div>
      <ServerDataTable
        isLoading={isFetching}
        columns={columns}
        data={data?.images}
        paginationInfo={data?.pagy}
        defaultColumnVisibility={{
          actions: hasPermission,
        }}
        extraButtons={
          <ResponsiveButton
            variant="secondary"
            disabled={exportMutation.isPending}
            onClick={handleExport}
            leftIcon={
              exportMutation.isPending ? (
                <Loader className="animate-spin" />
              ) : (
                <Download />
              )
            }
          >
            Download CSV
          </ResponsiveButton>
        }
        sortableColumns={["id", "file_name", "created_at"]}
      />
    </div>
  );
};
